import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import ErrorBoundary from '../../containers/error_boundary/error_boundary'
import { DevicesContext } from '../../contexts/devices_context'
import usePreviousProps from '../previous_props_hook'

type Props = {
  mediaStreamConstraints?: MediaStreamConstraints
  onStop?: any
  timer?: number
}

export const useMediaRecorder = ({
  mediaStreamConstraints: msc = {},
  onStop: handleStop,
  timer: timerDuration,
}: Props) => {
  const [state, setState] = useState<State>(State.IDLE)
  const [mediaStream, setMediaStream] = useState<MediaStream>()
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder>()
  const mediaChunks = useRef<Blob[]>([])
  const [mediaBlobUrl, setMediaBlobUrl] = useState<string | undefined>()
  const [previewVideoStream, setPreviewVideoStream] = useState<MediaStream>()
  const mediaStreamConstraints = useMemo(() => {
    // aspect ratio is flipped on mobile devices
    if (screen.width < screen.height) {
      return {
        ...msc,
        ...(msc.video && msc.video !== true && { video: { ...msc.video, aspectRatio: 1 / (msc.video.aspectRatio as number) }})
      }
    } else {
      return msc
    }
  }, [msc])
  const prevMediaStreamConstraints = usePreviousProps(mediaStreamConstraints)
  const unmounted = useRef(false)
  const [timer, setTimer] = useState<any>()

  const { getUserMedia } = useContext(DevicesContext)

  const setMediaStreams = useCallback((ms: MediaStream | undefined) => {
    setPreviewVideoStream(ms ? new MediaStream(ms.getVideoTracks()) : undefined)
    setMediaStream(ms)
  }, [])

  const setupPreview = useCallback(
    (constraints) => {
      setState(State.SETTING_UP_PREVIEW)
      getUserMedia(constraints).then(
        (stream) => {
          if (unmounted.current) {
            stopTracks(stream)
          } else {
            setMediaStreams(stream)
            setState(State.PREVIEW)
          }
        },
        () => {
          setState(State.ERROR)
        },
      )
    },
    [getUserMedia, setMediaStreams],
  )

  // when the constraints change, update the preview stream
  useEffect(() => {
    if (
      state === State.PREVIEW &&
      mediaStreamConstraints !== prevMediaStreamConstraints
    ) {
      setupPreview(mediaStreamConstraints)
    }
  }, [mediaStreamConstraints, prevMediaStreamConstraints, setupPreview, state])

  useEffect(() => {
    return () => {
      unmounted.current = true
    }
  }, [])

  // when the stream changes, stop all tracks
  useEffect(() => {
    if (mediaStream) {
      return () => {
        stopTracks(mediaStream)
      }
    }
  }, [mediaStream])

  // when the stream changes, stop all tracks
  useEffect(() => {
    if (previewVideoStream) {
      return () => {
        stopTracks(previewVideoStream)
      }
    }
  }, [previewVideoStream])

  const startRecording = useCallback(async () => {
    let stream = mediaStream
    if (!stream) {
      stream = await getUserMedia(mediaStreamConstraints)
      setMediaStreams(stream)
    }
    const mimeType = mediaStreamConstraints.video ? 'video/webm' : 'audio/webm'
    let mr
    try {
      mr = new MediaRecorder(stream, { mimeType })
    } catch {
      try {
        // fallback for iOS
        mr = new MediaRecorder(stream, { mimeType: 'video/mp4' })
      } catch (e: any) {
        ErrorBoundary.logErrorToMyService(e)
      }
    }
    setMediaRecorder(mr)
  }, [getUserMedia, mediaStream, mediaStreamConstraints, setMediaStreams])

  const stopRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      clearTimeout(timer)
      setTimer(undefined)
      mediaRecorder.stop()
    }
  }, [mediaRecorder, timer])

  useEffect(() => {
    if (mediaRecorder) {
      const onStart = () => {
        setState(State.RECORDING)
        if (timerDuration) {
          const t = setTimeout(stopRecording, timerDuration)
          setTimer((prevTimer: any) => {
            if (prevTimer) {
              clearTimeout(prevTimer)
            }
            return t
          })
        }
      }
      const onDataAvailable = ({ data }: BlobEvent) => {
        mediaChunks.current.push(data)
      }
      const onStop = () => {
        const blob = new Blob(mediaChunks.current, {
          type: mediaRecorder.mimeType.split(';')[0],
        })
        if (!blob.size) {
          mediaChunks.current = []
          mediaRecorder.start()
          return
        }
        const url = URL.createObjectURL(blob)
        handleStop(url, blob)
        setMediaBlobUrl(url)
        mediaChunks.current = []
        setMediaRecorder(undefined)
        if (mediaStream) {
          stopTracks(mediaStream)
          setMediaStreams(undefined)
        }
        setState(State.PLAYBACK)
      }
      mediaRecorder.addEventListener('start', onStart)
      mediaRecorder.addEventListener('dataavailable', onDataAvailable)
      mediaRecorder.addEventListener('stop', onStop)

      if (state === State.PREVIEW && mediaRecorder.state !== 'recording') {
        mediaRecorder.start()
      }

      return () => {
        mediaRecorder.removeEventListener('start', onStart)
        mediaRecorder.removeEventListener('dataavailable', onDataAvailable)
        mediaRecorder.removeEventListener('stop', onStop)
      }
    }
  }, [
    handleStop,
    mediaRecorder,
    mediaStream,
    setMediaStreams,
    state,
    stopRecording,
    timerDuration,
  ])

  const startPreview = useCallback(() => {
    if (state === State.IDLE) {
      setupPreview(mediaStreamConstraints)
    }
  }, [mediaStreamConstraints, setupPreview, state])

  const toggleRecording = useCallback(() => {
    if (state === State.RECORDING) {
      stopRecording()
      return Promise.resolve()
    } else {
      return startRecording()
    }
  }, [startRecording, state, stopRecording])

  const reset = useCallback(() => {
    if (mediaBlobUrl) {
      URL.revokeObjectURL(mediaBlobUrl)
    }
    setMediaBlobUrl(undefined)
    setState(State.IDLE)
  }, [mediaBlobUrl])

  const audioTrack = useMemo(() => {
    return mediaStream?.getAudioTracks()[0]
  }, [mediaStream])

  return {
    startPreview,
    toggleRecording,
    recording: state === State.RECORDING,
    previewVideoStream,
    mediaBlobUrl,
    reset,
    audioTrack,
  }
}

export enum State {
  IDLE,
  SETTING_UP_PREVIEW,
  PREVIEW,
  RECORDING,
  PLAYBACK,
  ERROR,
}

const stopTracks = (mediaStream: MediaStream) => {
  mediaStream.getTracks().forEach((track) => {
    track.stop()
  })
}
